import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { SearchFilter, IFilterResponse } from 'src/app/infrastructure/iFilter-component';
import { PagerControlComponent } from '../../controls/pager/pager-control.component';
import { MatDialog } from '@angular/material';
import { SearchFilterComponent } from '../../search-filter/search-filter.component';
import { MenuService } from 'src/app/services/menu.service';
import { PatientCrmSearchItem } from 'src/app/model/patient/patient-crm-search-item';
import { Router } from '@angular/router';
import { PatientService } from 'src/app/services/patient.service';
import { PatientSearch } from 'src/app/model/patient';
import { PatientEditDialogComponent } from '../../dialogs/patient-edit/patient-edit-dialog.component';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { SelectionModel } from '@angular/cdk/collections';
import { NotificationToastService } from 'src/app/services/notification-toast.service';

@Component({
  selector: 'app-patient-crm-search',
  templateUrl: './patient-crm-search.component.html',
  styleUrls: ['./patient-crm-search.component.css']
})
export class PatientCrmSearchComponent implements OnInit, OnDestroy {

  loaded: boolean;
  searchFilters: SearchFilter[];
  @ViewChild('pager', {static: true}) pager: PagerControlComponent;
  @ViewChild('searchFilter', {static: false}) searchFilter: SearchFilterComponent;

  columnsDefinition: { field: string; title: string }[] = [
    { field: 'LastName',     title: 'Apellido' },
    { field: 'FirstName',    title: 'Nombre' },
    { field: 'DocumentType', title: 'Tipo doc.' },
    { field: 'Number',       title: 'Documento' },
  ];

  _columnOrder: string[] = this.columnsDefinition.map(c => c.field);
  hiddenColumns: string[] = [];
  columnFilters: { [field: string]: string } = {};
  _sortColumn: string = '';
  _sortDirection: 'asc' | 'desc' = 'asc';
  columnWidths: { [field: string]: number } = {};
  _filteredData: PatientCrmSearchItem[] = [];
  dataSource: PatientCrmSearchItem[] = [];
  selection = new SelectionModel<PatientCrmSearchItem>(true, []);

  private _resizeMoveHandler: (e: MouseEvent) => void;
  private _resizeUpHandler: (e: MouseEvent) => void;
  private _resizingField: string = null;
  private _resizeStartX: number = 0;
  private _resizeStartWidth: number = 0;

  constructor(public dialog: MatDialog,
    private router: Router,
    private patientService: PatientService,
    private menuService: MenuService,
    private notificationToastService: NotificationToastService) {
    this.menuService.setMenuTitle('Paciente', 'CRM');
  }

  async validate(patientCrmSearchItem: PatientCrmSearchItem) {
    const toMergePatientIds = this.selection.selected
      .filter(item => item != null && item.Id != null && item.Id !== patientCrmSearchItem.Id)
      .map(item => item.Id);

    if (toMergePatientIds.length === 0) {
      this.notificationToastService.error('Debe seleccionar al menos un paciente para combinar', 'Paciente');
      return;
    }

    try {
      patientCrmSearchItem.Loading = true;
      await this.patientService.mergePatients(patientCrmSearchItem.Id, toMergePatientIds);
      this.notificationToastService.success('Los pacientes se combinaron correctamente', 'Paciente');
      this.selection.clear();

      if (this.searchFilter) {
        await this.searchFilter.search();
      }
    } catch (err) {
      this.notificationToastService.error(JSON.stringify(err.error), 'Paciente');
    } finally {
      patientCrmSearchItem.Loading = false;
    }
  }

  ngOnInit() {
    this.loadColumnState();
  }

  isAllSelected(): boolean {
    return this.dataSource.length > 0 && this.selection.selected.length === this.dataSource.length;
  }

  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.forEach(row => this.selection.select(row));
  }

  get orderedColumnsDefinition(): { field: string; title: string }[] {
    return this._columnOrder
      .filter(f => !this.hiddenColumns.includes(f))
      .map(f => this.columnsDefinition.find(c => c.field === f))
      .filter(c => c != null);
  }

  get availableColumns(): { field: string; title: string }[] {
    return this._columnOrder
      .map(f => this.columnsDefinition.find(c => c.field === f))
      .filter(c => c != null);
  }

  get exportColumns(): string[] {
    return this.orderedColumnsDefinition.map(c => c.field);
  }

  get exportColumnsName(): string[] {
    return this.orderedColumnsDefinition.map(c => c.title);
  }

  formatCellValue(element: PatientCrmSearchItem, field: string): string {
    if (element == null) return '';
    const val = (element as any)[field];
    return val != null ? String(val) : '';
  }

  getSortableValue(element: PatientCrmSearchItem, field: string): any {
    return this.formatCellValue(element, field);
  }

  applyColumnFilters() {
    let filtered = this._filteredData != null ? this._filteredData.slice() : [];
    Object.keys(this.columnFilters).forEach(field => {
      const term = this.columnFilters[field];
      if (term && term.trim() !== '') {
        const lower = term.toLowerCase();
        filtered = filtered.filter(element => {
          const val = this.formatCellValue(element, field);
          return val.toLowerCase().includes(lower);
        });
      }
    });
    if (this._sortColumn) {
      filtered.sort((a, b) => {
        const av = this.getSortableValue(a, this._sortColumn);
        const bv = this.getSortableValue(b, this._sortColumn);
        if (av < bv) return this._sortDirection === 'asc' ? -1 : 1;
        if (av > bv) return this._sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    this.dataSource = filtered;
  }

  loadColumnState() {
    try {
      const orderRaw = sessionStorage.getItem('patient-crm-search.columns.v1');
      if (orderRaw) {
        const saved: string[] = JSON.parse(orderRaw);
        const valid = saved.filter(f => this.columnsDefinition.some(c => c.field === f));
        const missing = this.columnsDefinition.map(c => c.field).filter(f => !valid.includes(f));
        this._columnOrder = valid.concat(missing);
      }
      const sortRaw = sessionStorage.getItem('patient-crm-search.sort');
      if (sortRaw) {
        const sort = JSON.parse(sortRaw);
        this._sortColumn = sort.column != null ? sort.column : '';
        this._sortDirection = sort.direction != null ? sort.direction : 'asc';
      }
      const widthsRaw = sessionStorage.getItem('patient-crm-search.column-widths.v1');
      if (widthsRaw) {
        this.columnWidths = JSON.parse(widthsRaw);
      }
    } catch (e) {}
  }

  saveColumnOrder() {
    sessionStorage.setItem('patient-crm-search.columns.v1', JSON.stringify(this._columnOrder));
  }

  saveSort() {
    sessionStorage.setItem('patient-crm-search.sort', JSON.stringify({ column: this._sortColumn, direction: this._sortDirection }));
  }

  saveColumnWidths() {
    sessionStorage.setItem('patient-crm-search.column-widths.v1', JSON.stringify(this.columnWidths));
  }

  isColumnVisible(field: string): boolean {
    return !this.hiddenColumns.includes(field);
  }

  setColumnVisibility(field: string, visible: boolean) {
    if (visible) {
      this.hiddenColumns = this.hiddenColumns.filter(f => f !== field);
    } else {
      if (!this.hiddenColumns.includes(field)) {
        this.hiddenColumns.push(field);
      }
    }
    this.applyColumnFilters();
  }

  showAllColumns() {
    this.hiddenColumns = [];
    this.applyColumnFilters();
  }

  getColumnWidth(field: string): number {
    return this.columnWidths[field] != null ? this.columnWidths[field] : 150;
  }

  sortByColumn(field: string) {
    if (this._sortColumn === field) {
      this._sortDirection = this._sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this._sortColumn = field;
      this._sortDirection = 'asc';
    }
    this.saveSort();
    this.applyColumnFilters();
  }

  getSortIcon(field: string): string {
    if (this._sortColumn !== field) return 'unfold_more';
    return this._sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  onColumnFilterInput(field: string, value: string) {
    this.columnFilters[field] = value;
    this.applyColumnFilters();
  }

  onHeaderDragStart(event: DragEvent, field: string) {
    event.dataTransfer.setData('text/plain', field);
    event.dataTransfer.effectAllowed = 'move';
  }

  onHeaderDragOver(event: DragEvent) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }

  onHeaderDrop(event: DragEvent, targetField: string) {
    event.preventDefault();
    const sourceField = event.dataTransfer.getData('text/plain');
    if (sourceField === targetField) return;
    const fromIndex = this._columnOrder.indexOf(sourceField);
    const toIndex = this._columnOrder.indexOf(targetField);
    if (fromIndex < 0 || toIndex < 0) return;
    moveItemInArray(this._columnOrder, fromIndex, toIndex);
    this.saveColumnOrder();
    this.applyColumnFilters();
  }

  onHeaderDragEnd() {}

  startResize(event: MouseEvent, field: string) {
    event.preventDefault();
    event.stopPropagation();
    this._resizingField = field;
    this._resizeStartX = event.clientX;
    this._resizeStartWidth = this.getColumnWidth(field);
    this._resizeMoveHandler = (e: MouseEvent) => {
      if (this._resizingField == null) return;
      const delta = e.clientX - this._resizeStartX;
      const newWidth = Math.max(50, this._resizeStartWidth + delta);
      this.columnWidths = Object.assign({}, this.columnWidths, { [this._resizingField]: newWidth });
    };
    this._resizeUpHandler = (e: MouseEvent) => {
      if (this._resizingField != null) {
        this.saveColumnWidths();
        this._resizingField = null;
      }
      window.removeEventListener('mousemove', this._resizeMoveHandler);
      window.removeEventListener('mouseup', this._resizeUpHandler);
    };
    window.addEventListener('mousemove', this._resizeMoveHandler);
    window.addEventListener('mouseup', this._resizeUpHandler);
  }

  ngOnDestroy() {
    if (this._resizeMoveHandler) window.removeEventListener('mousemove', this._resizeMoveHandler);
    if (this._resizeUpHandler) window.removeEventListener('mouseup', this._resizeUpHandler);
  }

  onStartSearch() {
  }

  selectedPageIndexChange(pagedItems: PatientCrmSearchItem[]) {
    this._filteredData = pagedItems != null ? pagedItems : [];
    this.applyColumnFilters();
    this.loaded = true;
  }

  onEndSearch(response: IFilterResponse<PatientCrmSearchItem>) {
    this.pager.setItems(response.items);
    this.searchFilters = response.searchFilters;
  }

  async disabled(patientCrmSearchItem: PatientCrmSearchItem) {
    await this.patientService.patientDisabled(patientCrmSearchItem.Id);
    await this.searchFilter.search();
  }

  async enabled(patientCrmSearchItem: PatientCrmSearchItem) {
    await this.patientService.patientEnabled(patientCrmSearchItem.Id);
    await this.searchFilter.search();
  }

  redirectoToWizard(patientCrmSearchItem: PatientCrmSearchItem) {
    this.router.navigate(['patient-crm-wizard', patientCrmSearchItem.Id]);
  }

  createPacient(): void {
    const dialogRef = this.dialog.open(PatientEditDialogComponent, {
      width: '90%',
      data: {
        patientId: null,
        firstName: '',
        lastName: '',
        document: ''
      }
    });

    dialogRef.afterClosed().subscribe((patientSearch: PatientSearch) => {
      if (patientSearch) {
        this.router.navigate(['patient-crm-wizard', patientSearch.Id]);
      }
    });
  }
}
